'use client';

import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { requestCountries } from '@/src/utils/countries';
import { initDebugger } from '@/src/utils/debugger';
import { initMaterializeExtras } from '@/src/utils/materializeExtras';
import { initScroller } from '@/src/utils/scroller';

import { ErrorBoundaryProvider } from './ErrorBoundaryProvider';
import { ToastProvider } from './ToastProvider';
import { ThemeProvider } from './ThemeProvider';
import { UserProvider } from './UserProvider';
import { Contact } from '@/src/utils/types';

const queryClient = new QueryClient();

interface ProviderProps {
  user: Contact | null;
  children: React.ReactNode;
}

export const Providers = ({ user, children }: ProviderProps) => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Add noUISlider to the global namespace
      initMaterializeExtras();

      // Initialize UX Debugger
      initDebugger();

      // Initialize our smooth scroller
      initScroller();

      // Attempt to initialize our country list before the application starts.
      requestCountries();
    }
  });

  return (
    <UserProvider user={user}>
      <ErrorBoundaryProvider>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>{children}</ToastProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ErrorBoundaryProvider>
    </UserProvider>
  );
};

export default Providers;
